import { useState, useMemo, type ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { RefreshCw, Clock, ShoppingBag, CalendarDays, CheckCircle2, Plus, Receipt, Download, Info, ArrowUpRight, ArrowDownRight, TrendingUp, Wallet, GlassWater, FileText, CreditCard, Landmark, Banknote, Gift, PieChart, Package, ListChecks } from "lucide-react";
import { useDashboardMetrics } from "@/features/dashboard";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/")({
  component: DashboardPage,
});

function fmt(n: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency", currency: "MXN", maximumFractionDigits: 0,
  }).format(n || 0);
}

const MESES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

const DIAS = ["Domingo","Lunes","Martes","Miércoles","Jueves","Viernes","Sábado"];

const C = {
  ingresos:   "#2F6D46",
  gastos:     "#B74A2E",
  cafeFuerte: "#090909",
  cafeBajito: "#B07A4F",
  gold:       "#FF6500",
  label:      "#111111",
};

const METODO_COLORS: Record<string, string> = {
  efectivo:      C.ingresos,
  transferencia: C.cafeFuerte,
  tarjeta:       "#4A7FB5",
};
function colorMetodo(m: string) {
  return METODO_COLORS[m.toLowerCase()] ?? C.cafeBajito;
}
/* Ícono según método de pago — tarjeta de crédito, banco (transferencia) o billete (efectivo) */
function iconMetodo(m: string) {
  const s = m.toLowerCase();
  if (s.includes("tarjeta"))  return CreditCard;
  if (s.includes("transfer")) return Landmark;
  return Banknote;
}
function colorProducto(nombre: string) {
  const n = nombre.toLowerCase();
  if (n.includes("litro"))  return C.cafeFuerte;
  if (n.includes("granel")) return "#E0D2CE";
  return C.cafeBajito;
}

/* Tarjeta KPI — banda 1 */
function Kpi({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="card-premium p-4">
      <p className="text-xs font-semibold text-muted-foreground leading-tight">{label}</p>
      <p className={cn("text-2xl font-extrabold tabular-nums mt-1 tracking-tight", accent ?? "text-foreground")}>
        {value}
      </p>
    </div>
  );
}

/* Encabezado de sección — mismo degradado del Hero, barrita lateral blanca, texto blanco */
function SectionHead({ title }: { title: string }) {
  return (
    <div
      className="flex items-center gap-2.5 rounded-xl px-4 py-2 shrink-0"
      style={{ background: "#090909" }}
    >
      <span className="h-3.5 w-1 rounded-full bg-white" />
      <h2 className="text-sm font-extrabold tracking-tight text-white">{title}</h2>
    </div>
  );
}

/* Encabezado interno — usado en Ingresos vs Gastos, Por producto y Gastos por tipo */
function MiniCardHead({ title }: { title: string }) {
  const HeadIcon =
    title === "Gastos por tipo" ? PieChart :
    title === "Por producto" ? Package :
    title === "Resumen de Pedidos" ? ListChecks :
    title === "Calendario" ? CalendarDays :
    title === "Regalos vs Ingresos" ? Gift :
    title === "Ingreso por método de pago" ? Wallet :
    TrendingUp;

  return (
    <div className="flex items-center justify-between px-4 py-2.5 bg-[#090909] lg:bg-[linear-gradient(135deg,#fff_0%,#fffaf6_58%,#fff0e7_100%)] lg:border-b lg:border-[#E9E3D9]">
      <div className="flex items-center gap-2">
        <span className="h-3.5 w-1 rounded-full bg-white lg:hidden" />
        <span className="hidden lg:flex size-7 items-center justify-center rounded-lg bg-[#FFF0E7] text-[#FF6500]">
          <HeadIcon className="size-4" strokeWidth={1.9} />
        </span>
        <h3 className="text-xs font-extrabold text-white lg:text-[#090909]">{title}</h3>
      </div>
      <Info className="size-3.5 text-white/40 lg:text-[#A39D94]" />
    </div>
  );
}

/* Tarjeta genérica */
function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("card-premium overflow-hidden lg:shadow-[0_10px_28px_rgba(9,9,9,0.045)]", className)}>
      {children}
    </div>
  );
}

const selectCls =
  "h-8 rounded-lg border border-input bg-card px-2 text-xs font-medium focus:outline-none cursor-pointer";

export default function DashboardPage() {
  const now = new Date();
  const [mode, setMode]   = useState<"mes" | "todos">("mes");
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const years = useMemo(() => {
  const y = now.getFullYear();
  return [y, y + 1, y + 2, y + 3];
}, [now]);
  
  // Saludo dinámico: antes de las 12pm "Buenos días", de las 12pm en adelante "Buenas tardes"
  const saludo   = now.getHours() < 12 ? "Buenos días" : "Buenas tardes";
  const fechaHoy = `${DIAS[now.getDay()]} ${now.getDate()} de ${(MESES[now.getMonth()] ?? "").toLowerCase()}`;

  const { desde, hasta } = useMemo(() => {
    if (mode === "todos") return { desde: "2000-01-01", hasta: "2999-12-31" };
    const mm      = String(month).padStart(2, "0");
    const lastDay = new Date(year, month, 0).getDate();
    return { desde: `${year}-${mm}-01`, hasta: `${year}-${mm}-${String(lastDay).padStart(2, "0")}` };
  }, [mode, year, month]);

  const queryClient = useQueryClient();
  const { data: proximos = [] } = useQuery<any[]>({
  queryKey: ["calendario", "proximos"],
  queryFn:  () => api.get("/calendario/proximos?limit=8"),
  staleTime: 5 * 60 * 1000,
});
  
  const marcarEntregado = useMutation({
    mutationFn: (id: number) =>
      api.put(`/pedidos/${id}`, { status: "entregado" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
    },
  });

  const marcarPagado = useMutation({
    mutationFn: (id: number) =>
      api.put(`/pedidos/${id}`, { pagado: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
    },
  });

  const marcarHecho = useMutation({
  mutationFn: (id: number) =>
    api.put(`/calendario/${id}`, { status: "completado" }),
  onSuccess: () =>
    queryClient.invalidateQueries({ queryKey: ["calendario", "proximos"] }),
});

  const { data: m, isLoading, isError, refetch, isFetching } =
    useDashboardMetrics(desde, hasta);

  // Métrica acumulada fija: siempre consulta todo el historial, independientemente
  // del filtro activo del dashboard. Esto permite comparar periodo vs. total.
  const {
    data: totalAcumulado,
    isLoading: isLoadingTotal,
    isError: isErrorTotal,
    refetch: refetchTotal,
    isFetching: isFetchingTotal,
  } = useDashboardMetrics("2000-01-01", "2999-12-31");
  
  if (isLoading || isLoadingTotal) return (
    <div className="bg-[#F5F3ED] h-full flex items-center justify-center gap-2 text-sm text-muted-foreground">
      <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      Cargando…
    </div>
  );
  if (isError || isErrorTotal || !m || !totalAcumulado) return (
    <div className="bg-[#F5F3ED] h-full flex flex-col items-center justify-center gap-2">
      <p className="text-sm text-destructive">Error al cargar el dashboard.</p>
      <button onClick={() => refetch()} className="text-sm text-primary hover:underline">Reintentar</button>
    </div>
  );

  /* Porcentajes para la tarjeta "Ingresos vs Gastos" rediseñada */
  const totalFin      = m.finanzas.ingresos_cobrados + m.finanzas.gastos_total;
  const pctIngresos    = totalFin > 0 ? Math.round((m.finanzas.ingresos_cobrados / totalFin) * 100) : 0;
  const pctGastosFin   = totalFin > 0 ? 100 - pctIngresos : 0;

  /* Insights rápidos del Hero: producto estrella + margen del periodo */
  const productoTop = m.ingresos_por_producto.length > 0
    ? m.ingresos_por_producto.reduce((a, b) => (b.porcentaje > a.porcentaje ? b : a))
    : null;
  const margenPct = m.finanzas.ingresos_cobrados > 0
    ? Math.round((m.finanzas.utilidad / m.finanzas.ingresos_cobrados) * 100)
    : 0;

  /* Venta vs Regalo — para el anillo de la tarjeta "Regalos vs Ingresos" */
  const ventaSplit  = m.regalos.split.find((s) => s.tipo === "venta");
  const regaloSplit = m.regalos.split.find((s) => s.tipo === "regalo");
  const ventaPct    = ventaSplit?.porcentaje ?? 0;
  const metodoData: { metodo: string; monto: number; porcentaje: number }[] =
    (m as any).ingresos_por_metodo ?? [];

  return (
    /*
      lg:h-screen lg:overflow-hidden → en escritorio fuerza el dashboard a la
      altura exacta de la pantalla sin scroll. En móvil fluye normal.
    */
    <div className="bg-[#F5F3ED] relative isolate flex flex-col p-3 sm:p-4 gap-4 lg:min-h-screen">

      {/* Marca de agua local */}
      <img
        src="/logo_sin_fondo.png"
        alt=""
        aria-hidden
        className="pointer-events-none select-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[55%] max-w-[480px] opacity-[0.05]"
        style={{ zIndex: -1 }}
      />

      {/* ─── Hero / Resumen principal ───────────────────────────── */}
     <section className="sticky top-3 z-40 relative overflow-hidden rounded-[26px] border border-[#E7E2D8] bg-[#FBFAF6]/95 backdrop-blur-md p-5 sm:p-6 lg:p-7 shrink-0 shadow-[0_14px_38px_rgba(9,9,9,0.08)]">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-70"
          style={{
            background: "radial-gradient(circle at 86% 18%, rgba(255,101,0,.075), transparent 27%), radial-gradient(circle at 8% 90%, rgba(9,9,9,.025), transparent 28%)",
          }}
        />

        <div className="relative grid gap-5 xl:grid-cols-[minmax(300px,1.05fr)_minmax(0,2.35fr)] xl:items-stretch">
          {/* Saludo e información operativa */}
          <div className="flex flex-col justify-center min-w-0">
            <p className="text-[11px] font-extrabold tracking-[0.12em] uppercase text-[#66645F]">
              {fechaHoy}
            </p>
            <h1 className="text-2xl sm:text-[30px] font-extrabold tracking-[-0.035em] text-[#090909] mt-1">
              {saludo}, Crew ☕
            </h1>
            <p className="text-xs sm:text-sm text-[#66645F] mt-1.5">
              {mode === "todos" ? "Todos los periodos" : `${MESES[month - 1]} ${year}`} · {m.pedidos.por_entregar} pedidos por entregar
              {m.pedidos.lista_por_cobrar.length > 0 && ` · ${m.pedidos.lista_por_cobrar.length} por cobrar`}
            </p>
            {productoTop && (
              <p className="text-xs text-[#66645F] mt-2 flex items-center gap-1.5">
                <span className="text-[#FF6500]">★</span>
                {productoTop.producto_nombre} es tu producto estrella · {productoTop.porcentaje}% de tus ingresos
              </p>
            )}
          </div>

          {/* Comparativo financiero: periodo seleccionado vs. acumulado */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
            <article className="rounded-[20px] border border-[#E7E2D8] bg-white p-4 flex flex-col justify-between min-h-[126px]">
              <div className="flex items-start justify-between gap-3">
                <p className="text-xs font-bold text-[#575550]">
                  {mode === "todos" ? "Ingresos totales" : "Ingresos del mes"}
                </p>
                <span className="size-9 rounded-full bg-[#FFF0E5] text-[#FF6500] flex items-center justify-center shrink-0">
                  <TrendingUp className="size-4.5" />
                </span>
              </div>
              <p className="text-2xl sm:text-[28px] font-extrabold tracking-tight tabular-nums text-[#090909]">
                {fmt(m.finanzas.ingresos_cobrados)}
              </p>
              <p className="text-[10px] text-[#8B8881]">Cobrado en el periodo</p>
            </article>

            <article className="rounded-[20px] border border-[#E7E2D8] bg-white p-4 flex flex-col justify-between min-h-[126px]">
              <div className="flex items-start justify-between gap-3">
                <p className="text-xs font-bold text-[#575550]">
                  {mode === "todos" ? "Gastos totales" : "Gastos del mes"}
                </p>
                <span className="size-9 rounded-full bg-[#FFF0EC] text-[#D94A28] flex items-center justify-center shrink-0">
                  <ArrowDownRight className="size-4.5" />
                </span>
              </div>
              <p className="text-2xl sm:text-[28px] font-extrabold tracking-tight tabular-nums text-[#090909]">
                {fmt(m.finanzas.gastos_total)}
              </p>
              <p className="text-[10px] text-[#8B8881]">Registrado en el periodo</p>
            </article>

            <article className="rounded-[20px] border border-[#FFDCC5] bg-[#FFF6EF] p-4 flex flex-col justify-between min-h-[126px]">
              <div className="flex items-start justify-between gap-3">
                <p className="text-xs font-bold text-[#575550]">
                  {mode === "todos" ? "Utilidad del periodo" : "Utilidad del mes"}
                </p>
                <span className="size-9 rounded-full bg-[#FFE6D5] text-[#FF6500] flex items-center justify-center shrink-0">
                  <Wallet className="size-4.5" />
                </span>
              </div>
              <p className={cn(
                "text-2xl sm:text-[28px] font-extrabold tracking-tight tabular-nums",
                m.finanzas.utilidad >= 0 ? "text-[#2F6D46]" : "text-[#E95800]"
              )}>
                {fmt(m.finanzas.utilidad)}
              </p>
              <p className="text-[10px] text-[#8B8881]">Margen {margenPct}%</p>
            </article>

            <article className="rounded-[20px] border border-[#FFD7BC] bg-[#FFF0E5] p-4 flex flex-col justify-between min-h-[126px] relative overflow-hidden">
              <div aria-hidden className="absolute -right-6 -top-6 size-24 rounded-full bg-[#FF6500]/[0.06]" />
              <div className="relative flex items-start justify-between gap-3">
                <p className="text-xs font-extrabold text-[#090909]">Utilidad total</p>
                <span className="size-9 rounded-full bg-white/80 text-[#FF6500] flex items-center justify-center shrink-0 border border-[#FFD7BC]">
                  <Wallet className="size-4.5" />
                </span>
              </div>
              <p className={cn(
                "relative text-2xl sm:text-[28px] font-extrabold tracking-tight tabular-nums",
                totalAcumulado.finanzas.utilidad >= 0 ? "text-[#090909]" : "text-[#E95800]"
              )}>
                {fmt(totalAcumulado.finanzas.utilidad)}
              </p>
              <p className="relative text-[10px] font-medium text-[#7B675A]">Acumulada · todos los periodos</p>
            </article>
          </div>
        </div>

        {/* Acciones y filtros */}
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mt-5 pt-4 border-t border-[#E7E2D8]">
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/pedidos/nuevo"
              className="cc-interactive flex items-center gap-1.5 text-[12.5px] font-extrabold px-4 py-2.5 rounded-xl bg-[#FF6500] text-[#090909] hover:bg-[#E95800]">
              <Plus className="size-3.5" /> Nuevo pedido
            </Link>
            <Link to="/gastos/nuevo"
              className="cc-interactive flex items-center gap-1.5 text-[12.5px] font-bold px-4 py-2.5 rounded-xl bg-white text-[#090909] border border-[#DEDCD5] hover:border-[#AAA7A0] hover:bg-[#F5F3ED]">
              <Receipt className="size-3.5" /> Registrar gasto
            </Link>
            <Link to="/exportar"
              className="cc-interactive flex items-center gap-1.5 text-[12.5px] font-bold px-4 py-2.5 rounded-xl bg-white text-[#090909] border border-[#DEDCD5] hover:border-[#AAA7A0] hover:bg-[#F5F3ED]">
              <Download className="size-3.5" /> Exportar
            </Link>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex rounded-xl border border-[#DEDCD5] bg-white overflow-hidden p-0.5">
              {(["mes", "todos"] as const).map((opt) => (
                <button key={opt} onClick={() => setMode(opt)}
                  className={cn(
                    "px-3.5 py-1.5 rounded-[9px] text-xs font-bold transition-all",
                    mode === opt ? "bg-[#090909] text-white shadow-sm" : "text-[#66645F] hover:bg-[#F5F3ED] hover:text-[#090909]"
                  )}>
                  {opt === "mes" ? "Por mes" : "Todos"}
                </button>
              ))}
            </div>
            {mode === "mes" && (
              <>
                <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
                  className="h-9 rounded-xl border border-[#DEDCD5] bg-white text-[#090909] px-2.5 text-xs font-semibold focus:outline-none focus:border-[#FF6500] cursor-pointer">
                  {MESES.map((mes, i) => <option key={i} value={i + 1}>{mes}</option>)}
                </select>
                <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                  className="h-9 rounded-xl border border-[#DEDCD5] bg-white text-[#090909] px-2.5 text-xs font-semibold focus:outline-none focus:border-[#FF6500] cursor-pointer">
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </>
            )}
            <button
              onClick={() => { refetch(); refetchTotal(); }}
              disabled={isFetching || isFetchingTotal}
              className="cc-interactive size-9 rounded-xl border border-[#DEDCD5] bg-white text-[#575550] hover:text-[#FF6500] hover:border-[#FF6500] flex items-center justify-center disabled:opacity-50"
              aria-label="Actualizar dashboard"
            >
              <RefreshCw className={cn("size-3.5", (isFetching || isFetchingTotal) && "animate-spin")} />
            </button>
          </div>
        </div>
      </section>

      {/* ══ ZONA FINANCIERA ═══════════════════════════════════════ */}
      <section className="flex flex-col gap-3 shrink-0">
        <div className="flex items-end justify-between gap-4 px-1 pt-1">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#FF6500]">Análisis financiero</p>
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-[-0.03em] text-[#090909]">Ingresos</h2>
            <p className="text-xs text-[#77736C] mt-0.5">Cobrado, pendiente, estimado y participación por producto.</p>
          </div>
          <div className="hidden sm:block text-right">
            <p className="text-[10px] font-semibold text-[#77736C]">Cobrado en el periodo</p>
            <p className="text-2xl font-extrabold tabular-nums text-[#2F6D46]">{fmt(m.finanzas.ingresos_cobrados)}</p>
          </div>
        </div>

        {/* Línea 1: KPIs (Utilidad ahora vive en el Hero de arriba) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          <Kpi label="Cobrado" value={fmt(m.finanzas.ingresos_cobrados)} accent="text-[#2F6D46]" />
          <Kpi label="Pendiente de cobro" value={fmt(m.finanzas.ingresos_pendientes)} accent="text-[#B5821F]" />
          <Kpi label="Ingresos estimados" value={fmt(m.finanzas.ingresos_total)} />
          <Kpi label="Ganancia neta" value={fmt(m.finanzas.utilidad)} accent={m.finanzas.utilidad >= 0 ? "text-[#2F6D46]" : "text-[#E95800]"} />
        </div>

        <div className="flex items-end justify-between gap-4 px-1 pt-3 mt-1 border-t border-[#DEDCD5]">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#B74A2E]">Control de egresos</p>
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-[-0.03em] text-[#090909]">Gastos</h2>
            <p className="text-xs text-[#77736C] mt-0.5">Comparativo financiero y distribución por categoría.</p>
          </div>
          <div className="hidden sm:block text-right">
            <p className="text-[10px] font-semibold text-[#77736C]">Registrado en el periodo</p>
            <p className="text-2xl font-extrabold tabular-nums text-[#B74A2E]">{fmt(m.finanzas.gastos_total)}</p>
          </div>
        </div>

        {/* Análisis: comparativo / producto / gastos por tipo */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.02fr_1.08fr_1fr] gap-3 items-stretch">

          {/* ── Tarjeta: Ingresos vs Gastos ──────────────────────────────── */}
          <Card className="overflow-hidden h-full flex flex-col lg:bg-[linear-gradient(145deg,#ffffff_0%,#fffdfa_58%,#fff3ec_100%)]">
            <MiniCardHead title="Ingresos vs Gastos" />
            <div className="p-3.5 flex-1 flex flex-col justify-between">
              {/* Bloques Ingresos / Gastos */}
              <div className="grid grid-cols-2 gap-2.5">
                <div className="rounded-2xl p-3 flex flex-col items-center text-center gap-1.5" style={{ background: "#EEF5F0" }}>
                  <div className="size-9 rounded-full flex items-center justify-center border-2" style={{ borderColor: "#071A07" }}>
                    <ArrowUpRight className="size-4" style={{ color: "#071A07" }} />
                  </div>
                  <p className="text-xs font-bold" style={{ color: "#071A07" }}>Ingresos</p>
                  <p className="text-xl font-extrabold tabular-nums" style={{ color: "#071A07" }}>{fmt(m.finanzas.ingresos_cobrados)}</p>
                  <span className="text-[11px] font-bold text-white px-2.5 py-0.5 rounded-full" style={{ background: "#071A07" }}>
                    {pctIngresos}%
                  </span>
                  <p className="text-[10px] text-muted-foreground">del total</p>
                </div>
                <div className="rounded-2xl p-3 flex flex-col items-center text-center gap-1.5" style={{ background: "#FBEEE9" }}>
                  <div className="size-9 rounded-full flex items-center justify-center border-2" style={{ borderColor: C.gastos }}>
                    <ArrowDownRight className="size-4" style={{ color: C.gastos }} />
                  </div>
                  <p className="text-xs font-bold" style={{ color: C.gastos }}>Gastos</p>
                  <p className="text-xl font-extrabold tabular-nums" style={{ color: C.gastos }}>{fmt(m.finanzas.gastos_total)}</p>
                  <span className="text-[11px] font-bold text-white px-2.5 py-0.5 rounded-full" style={{ background: C.gastos }}>
                    {pctGastosFin}%
                  </span>
                  <p className="text-[10px] text-muted-foreground">del total</p>
                </div>
              </div>

              {/* Barra de proporción Ingresos vs Gastos */}
              <div className="h-2 rounded-full bg-muted overflow-hidden mt-3 flex">
                <div className="h-full" style={{ width: `${pctIngresos}%`, background: "#071A07" }} />
                <div className="h-full" style={{ width: `${pctGastosFin}%`, background: C.gastos }} />
              </div>
              <div className="flex justify-between text-[11px] font-semibold text-muted-foreground mt-1">
                <span>{pctIngresos}%</span>
                <span>{pctGastosFin}%</span>
              </div>

              {/* Ganancia neta (= Utilidad) */}
              <div className="mt-3 rounded-xl p-3 flex items-center justify-between"
                style={{ background: m.finanzas.utilidad >= 0 ? "#EEF5F0" : "#FBEEE9" }}>
                <span className="flex items-center gap-2 text-xs font-bold" style={{ color: m.finanzas.utilidad >= 0 ? "#071A07" : C.gastos }}>
                  <TrendingUp className="size-4" /> Ganancia neta
                </span>
                <span className="text-lg font-extrabold tabular-nums" style={{ color: m.finanzas.utilidad >= 0 ? "#071A07" : C.gastos }}>
                  {fmt(m.finanzas.utilidad)}
                </span>
              </div>
            </div>
          </Card>

         {/* ── Tarjeta: Por producto ────────────────────────────────────── */}
<Card className="overflow-hidden h-full flex flex-col lg:bg-[linear-gradient(145deg,#ffffff_0%,#fffdfa_62%,#f8f5ef_100%)]">
  <MiniCardHead title="Por producto" />

  <div className="p-3.5 flex-1 flex flex-col">

    {/* Encabezado interno */}
    <div className="flex items-center justify-between gap-3 mb-3">
      <div>
        <p className="text-xs font-bold text-[#090909]">
          Participación en ingresos
        </p>
        <p className="text-[10px] text-[#8B8881] mt-0.5">
          Distribución por producto
        </p>
      </div>

      <div className="flex items-center rounded-lg border border-[#DEDCD5] bg-[#F5F3ED] p-0.5 shrink-0">
        <span className="rounded-md bg-[#090909] px-2.5 py-1 text-[10px] font-bold text-white">
          Ingresos
        </span>

        <span className="px-2.5 py-1 text-[10px] font-semibold text-[#66645F]">
          Unidades
        </span>
      </div>
    </div>

    {m.ingresos_por_producto.length === 0 ? (
      <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center">
        <div className="size-11 rounded-full bg-muted flex items-center justify-center">
          <Wallet className="size-5 text-muted-foreground" />
        </div>

        <p className="text-xs text-muted-foreground">
          Sin ingresos.
        </p>
      </div>
    ) : (
      <div className="flex flex-col gap-2.5 flex-1">
        {m.ingresos_por_producto.map((d, i) => {
          const color = colorProducto(d.producto_nombre);
          const esGranel = d.producto_nombre
            .toLowerCase()
            .includes("granel");

          const Icon = esGranel
            ? ShoppingBag
            : GlassWater;

          const textoPorcentaje =
            color === "#E0D2CE"
              ? "#090909"
              : "#FFFFFF";

          return (
            <div
              key={i}
              className="rounded-2xl border border-[#DEDCD5] bg-white p-3"
            >
              <div className="grid grid-cols-[52px_34px_minmax(0,1fr)_auto] items-center gap-3">

                {/* Porcentaje */}
                <div
                  className="h-[52px] rounded-xl flex items-center justify-center text-sm font-extrabold tabular-nums"
                  style={{
                    background: color,
                    color: textoPorcentaje,
                  }}
                >
                  {d.porcentaje}%
                </div>

                {/* Icono */}
                <div className="flex items-center justify-center">
                  <Icon
                    className="size-7 text-[#66645F]"
                    strokeWidth={1.6}
                  />
                </div>

                {/* Producto + barra */}
                <div className="min-w-0">

                  <div className="h-2.5 rounded-full bg-[#ECEAE4] overflow-hidden mb-2">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${d.porcentaje}%`,
                        background: color,
                      }}
                    />
                  </div>

                  <div className="flex items-end justify-between gap-2">

                    <div className="min-w-0">
                      <p className="text-xs font-extrabold text-[#090909] truncate">
                        {d.producto_nombre}
                      </p>

                      <p className="text-[10px] text-[#8B8881] mt-0.5">
                        {d.unidades != null
                          ? `${d.unidades} u`
                          : "—"}
                      </p>
                    </div>

                  </div>
                </div>

                {/* Importe */}
                <p className="text-sm lg:text-base font-extrabold tabular-nums text-[#090909] whitespace-nowrap">
                  {fmt(d.ingresos)}
                </p>

              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
</Card>

          {/* ── Tarjeta: Gastos por tipo ──────────────────────────────────── */}
          <Card className="overflow-hidden h-full flex flex-col lg:bg-[linear-gradient(145deg,#ffffff_0%,#fffdfa_58%,#fff3ec_100%)]">
            <MiniCardHead title="Gastos por tipo" />
            <div className="p-3.5 flex-1 flex flex-col justify-center">
              {m.gastos_por_categoria.length === 0
                ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                    <div className="size-11 rounded-full bg-muted flex items-center justify-center">
                      <Wallet className="size-5 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground">Sin gastos.</p>
                  </div>
                )
                : (() => {
                    const GASTO_COLORS: Record<string, string> = {
                      "Café":            "#C4A882",
                      "Agua":            "#A8D5F5",
                      "Vainilla":        "#F5D5A8",
                      "Conservador":     "#E0E0E0",
                      "Etiquetas":       "#A8D5B0",
                      "Botellas":        "#C8C8C8",
                      "Contenido redes": "#C5B3E6",
                      "Otro":            "#F5C5D5",
                    };
                    return (
                      <div className="space-y-3">
                        {m.gastos_por_categoria.map((g, i) => {
                          const color = GASTO_COLORS[g.label] ?? "#E0E0E0";
                          return (
                            <div key={i}>
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="flex items-center gap-1.5 font-bold text-foreground">
                                  <span className="size-2.5 rounded-full shrink-0" style={{ background: color }} />
                                  {g.label}
                                </span>
                                <span className="font-extrabold tabular-nums">{g.porcentaje}%</span>
                              </div>
                              <div className="h-2 rounded-full bg-muted overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${g.porcentaje}%`, background: color }} />
                              </div>
                              <div className="text-[11px] text-muted-foreground mt-1 text-right font-semibold">
                                {fmt(g.monto)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
            </div>
          </Card>
        </div>
      </section>

      {/* ══ OPERACIÓN — TODO LO DEMÁS ═════════════════════════════ */}
      <section className="flex flex-col gap-3">
        <div className="flex items-end justify-between gap-4 px-1 pt-1">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#66645F]">Operación</p>
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-[-0.03em] text-[#090909]">Todo lo demás</h2>
            <p className="text-xs text-[#77736C] mt-0.5">Pedidos, calendario, regalos y métodos de pago.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:min-h-[430px] items-stretch">

        {/* 1 — Resumen de pedidos (listas Por entregar / Por cobrar funcionan exactamente igual que antes) */}
        <Card className="overflow-hidden flex flex-col h-full lg:bg-[linear-gradient(145deg,#ffffff_0%,#fffdfa_62%,#f4faf6_100%)]">
          <MiniCardHead title="Resumen de Pedidos" />
          <div className="p-3.5 flex flex-col gap-2.5 flex-1 min-h-0">
            {/* Ícono-stats: Entregados / Por entregar / Total */}
            <div className="grid grid-cols-3 gap-1.5 shrink-0">
              <div className="rounded-xl border border-border/70 bg-muted/30 p-2 flex flex-col items-center gap-1 text-center">
                <div className="size-8 rounded-full flex items-center justify-center" style={{ background: "#EEF5F0" }}>
                  <CheckCircle2 className="size-4" style={{ color: "#2F6D46" }} />
                </div>
                <p className="text-[9.5px] font-semibold text-muted-foreground leading-none">Entregados</p>
                <p className="text-base font-extrabold tabular-nums" style={{ color: "#2F6D46" }}>{m.pedidos.entregados}</p>
              </div>
              <div className="rounded-xl border border-border/70 bg-muted/30 p-2 flex flex-col items-center gap-1 text-center">
                <div className="size-8 rounded-full flex items-center justify-center" style={{ background: "#FBF3E6" }}>
                  <Clock className="size-4" style={{ color: "#B5821F" }} />
                </div>
                <p className="text-[9.5px] font-semibold text-muted-foreground leading-none">Por entregar</p>
                <p className="text-base font-extrabold tabular-nums" style={{ color: "#B5821F" }}>{m.pedidos.por_entregar}</p>
              </div>
              <div className="rounded-xl border border-border/70 bg-muted/30 p-2 flex flex-col items-center gap-1 text-center">
                <div className="size-8 rounded-full flex items-center justify-center bg-muted">
                  <FileText className="size-4 text-foreground" />
                </div>
                <p className="text-[9.5px] font-semibold text-muted-foreground leading-none">Total</p>
                <p className="text-base font-extrabold tabular-nums">{m.pedidos.total}</p>
              </div>
            </div>

            {/* Por entregar — misma lista y mutación de siempre, solo cambia el empaque visual */}
            <div className="rounded-xl border border-border p-3 flex flex-col min-h-[140px] flex-1">
              <p className="text-xs font-bold shrink-0 mb-2 flex items-center gap-1.5">
                <ShoppingBag className="size-3.5" style={{ color: C.gastos }} /> Por entregar
              </p>
              {m.pedidos.lista_por_entregar.length === 0
                ? <p className="text-xs text-muted-foreground text-center my-auto">Nada pendiente.</p>
                : (
                  <ul className="divide-y divide-border overflow-auto scrollbar-thin flex-1">
                    {m.pedidos.lista_por_entregar.map((p) => (
                      <li key={p.id} className="py-2 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-sm font-bold block truncate">{p.cliente_nombre}</span>
                          <span className="text-xs text-muted-foreground truncate block">{p.productos || "—"}</span>
                        </div>
                        <button
                          onClick={() => marcarEntregado.mutate(p.id)}
                          disabled={marcarEntregado.isPending}
                          className="shrink-0 flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md bg-[#2F6D46]/10 text-[#2F6D46] hover:bg-[#2F6D46]/20 transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                          <CheckCircle2 className="size-3" /> Entregado
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
            </div>

            {/* Por cobrar — misma lista y mutación de siempre, solo cambia el empaque visual */}
            <div className="rounded-xl border border-border p-3 flex flex-col min-h-[140px] flex-1">
              <p className="text-xs font-bold shrink-0 mb-2 flex items-center gap-1.5">
                <Clock className="size-3.5" style={{ color: C.gastos }} /> Por cobrar
              </p>
              {m.pedidos.lista_por_cobrar.length === 0
                ? <p className="text-xs text-muted-foreground text-center my-auto">Todo cobrado.</p>
                : (
                  <ul className="divide-y divide-border overflow-auto scrollbar-thin flex-1">
                    {m.pedidos.lista_por_cobrar.map((p) => (
                      <li key={p.id} className="py-2 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <span className="text-sm font-bold truncate block">{p.cliente_nombre}</span>
                          <span className="tabular-nums text-xs font-bold" style={{ color: C.gastos }}>{fmt(p.monto)}</span>
                        </div>
                        <button
                          onClick={() => marcarPagado.mutate(p.id)}
                          disabled={marcarPagado.isPending}
                          className="shrink-0 flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md bg-[#2F6D46]/10 text-[#2F6D46] hover:bg-[#2F6D46]/20 transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                          <CheckCircle2 className="size-3" /> Pagado
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          </div>
        </Card>

        {/* 2 — Calendario */}
        <Card className="overflow-hidden flex flex-col h-full lg:bg-[linear-gradient(145deg,#ffffff_0%,#fffdfa_62%,#f7f5f0_100%)]">
          <MiniCardHead title="Calendario" />
          <div className="p-3.5 flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between shrink-0 mb-2">
              <p className="text-xs font-bold">Próximos recordatorios</p>
              <Link to="/calendario/nuevo"
                className="text-xs font-semibold text-[#090909] hover:underline">
                + Nuevo
              </Link>
            </div>
            {proximos.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <div className="size-11 rounded-full bg-muted flex items-center justify-center">
                  <CalendarDays className="size-5 text-muted-foreground" />
                </div>
                <p className="text-xs text-center">Sin recordatorios</p>
                <Link to="/calendario/nuevo"
                  className="text-xs font-semibold text-[#090909] hover:underline">
                  Crea el primero
                </Link>
              </div>
            ) : (
              <ul className="flex-1 min-h-0 overflow-auto scrollbar-thin divide-y divide-border">
                {proximos.map((ev: any) => (
                  <li key={ev.id} className="py-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className={cn(
                        "text-sm font-semibold truncate",
                        ev.status === "completado" && "line-through text-muted-foreground"
                      )}>
                        {ev.titulo}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {new Date(ev.fecha + "T00:00:00").toLocaleDateString("es-MX", {
                          weekday: "short", day: "numeric", month: "short",
                        })}
                        {ev.hora_inicio ? ` · ${ev.hora_inicio}` : ""}
                      </p>
                    </div>
                    {ev.status !== "completado" ? (
                      <button
                        onClick={() => marcarHecho.mutate(ev.id)}
                        disabled={marcarHecho.isPending}
                        className="shrink-0 text-[11px] font-bold px-2 py-1 rounded-md bg-[#2F6D46]/10 text-[#2F6D46] hover:bg-[#2F6D46]/20 transition-colors disabled:opacity-50"
                      >
                        ✓ Hecho
                      </button>
                    ) : (
                      <span className="shrink-0 text-[11px] px-2 py-1 rounded-md bg-muted text-muted-foreground">
                        ✓
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        {/* 3 — Regalos vs Ingresos — barras Venta/Regalo + footer de insight, igual a la referencia */}
        <Card className="overflow-hidden flex flex-col h-full lg:bg-[linear-gradient(145deg,#ffffff_0%,#fffdfa_58%,#fff3ec_100%)]">
          <MiniCardHead title="Regalos vs Ingresos" />
          <div className="p-3.5 flex flex-col gap-2.5 flex-1">
            {/* Total pedidos / Total regalos — en tarjetas con borde, igual que el resto de la Banda 2 */}
            <div className="grid grid-cols-2 gap-2 shrink-0">
              <div className="rounded-xl border border-border p-2.5 text-center">
                <p className="text-[10px] font-semibold text-muted-foreground">Total pedidos</p>
                <p className="text-lg font-extrabold tabular-nums mt-0.5">{m.regalos.total_pedidos}</p>
              </div>
              <div className="rounded-xl border border-border p-2.5 text-center">
                <p className="text-[10px] font-semibold text-muted-foreground">Total regalos</p>
                <p className="text-lg font-extrabold tabular-nums mt-0.5">{m.regalos.total_regalos}</p>
              </div>
            </div>

            {/* Venta vs Regalo — en su propia tarjeta con borde */}
            <div className="rounded-xl border border-border p-3.5 flex flex-col flex-1 min-h-[140px] gap-3">
              <p className="text-xs font-bold shrink-0">Venta vs Regalo</p>

              {/* Fila Ventas */}
              <div className="flex items-center gap-3">
                <div className="size-11 rounded-full flex items-center justify-center shrink-0" style={{ background: "#071A07" }}>
                  <ShoppingBag className="size-5 text-white" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold mb-1.5">Ventas</p>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${ventaPct}%`, background: "#071A07" }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xl font-extrabold tabular-nums leading-none">{ventaSplit?.count ?? 0}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">pedidos</p>
                  <p className="text-[11px] font-bold mt-0.5" style={{ color: "#071A07" }}>{fmt(m.finanzas.ingresos_total)}</p>
                </div>
              </div>

              {/* Fila Regalos */}
              <div className="flex items-center gap-3">
                <div className="size-11 rounded-full flex items-center justify-center shrink-0" style={{ background: "#FF6500" }}>
                  <Gift className="size-5 text-white" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold mb-1.5">Regalos</p>
                  <div className="relative h-2">
                    {/* pista punteada */}
                    <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 border-t-2 border-dashed" style={{ borderColor: "#E4D3AE" }} />
                    {/* tramo relleno */}
                    <div className="absolute left-0 top-0 h-2 rounded-full" style={{ width: `max(8px, ${regaloSplit?.porcentaje ?? 0}%)`, background: "#FF6500" }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xl font-extrabold tabular-nums leading-none">{regaloSplit?.count ?? 0}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">pedidos</p>
                  <p className="text-[11px] font-bold mt-0.5" style={{ color: "#FF6500" }}>{fmt(0)}</p>
                </div>
              </div>

              <div className="border-t border-border" />

              {/* Footer: insight + relación + % */}
              <div className="grid grid-cols-3 gap-2 items-center">
                <div className="flex items-center gap-2 rounded-xl p-2" style={{ background: "#EEF5F0" }}>
                  <div className="size-7 rounded-full flex items-center justify-center shrink-0" style={{ background: "#071A07" }}>
                    <CheckCircle2 className="size-4 text-white" />
                  </div>
                  <p className="text-[10.5px] font-semibold leading-tight" style={{ color: "#071A07" }}>
                    {(regaloSplit?.count ?? 0) === 0
                      ? "Ningún pedido fue registrado como regalo."
                      : `${regaloSplit?.count} pedido${(regaloSplit?.count ?? 0) > 1 ? "s" : ""} (${regaloSplit?.porcentaje}%) fueron regalo.`}
                  </p>
                </div>
                <div className="text-center border-x border-border px-1">
                  <p className="text-lg font-extrabold tabular-nums">{ventaSplit?.count ?? 0} : {regaloSplit?.count ?? 0}</p>
                  <p className="text-[10px] text-muted-foreground">Relación</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-extrabold tabular-nums">{ventaPct}%</p>
                  <p className="text-[10px] text-muted-foreground leading-tight">de los pedidos fueron ventas</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* 4 — Ingreso por método de pago */}
        <Card className="overflow-hidden flex flex-col h-full lg:bg-[linear-gradient(145deg,#ffffff_0%,#fffdfa_62%,#f4faf6_100%)]">
          <MiniCardHead title="Ingreso por método de pago" />
          <div className="p-3.5 flex flex-col gap-3 flex-1 justify-center">
            {metodoData.length === 0
              ? (
                <div className="flex flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                  <p className="text-sm">Sin ingresos cobrados en el periodo.</p>
                </div>
              )
              : (
                <div className="space-y-4">
                  {metodoData.map((d) => {
                    const Icon = iconMetodo(d.metodo);
                    return (
                      <div key={d.metodo} className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <Icon className="size-4 shrink-0" style={{ color: colorMetodo(d.metodo) }} />
                          <span className="text-sm font-bold capitalize flex-1">{d.metodo}</span>
                          <span className="tabular-nums text-sm font-extrabold">{fmt(d.monto)}</span>
                        </div>
                        <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${d.porcentaje}%`, background: colorMetodo(d.metodo) }} />
                        </div>
                        <p className="text-xs text-right font-semibold" style={{ color: colorMetodo(d.metodo) }}>
                          {d.porcentaje}%
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            {/* Total ingresos */}
            <div className="rounded-xl p-3 flex items-center justify-between" style={{ background: "#EEF5F0" }}>
              <span className="text-xs font-bold" style={{ color: "#071A07" }}>Total ingresos</span>
              <span className="text-lg font-extrabold tabular-nums" style={{ color: "#071A07" }}>{fmt(m.finanzas.ingresos_cobrados)}</span>
            </div>
          </div>
        </Card>
        </div>
      </section>
    </div>
  );
}


